/// <reference types="webpack-env" />

/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {
  ErrorHandler,
  Injectable,
  Injector,
  NgZone,
  InjectionToken,
  Provider,
  Testability,
  isDevMode,
  StaticProvider,
} from "@angular/core";

import { ON_WEB_WORKER, DEBUG } from "./web_workers/shared/api";
import { ClientMessageBrokerFactory } from "./web_workers/shared/client_message_broker";
import { MessageBus } from "./web_workers/shared/message_bus";
import { PrimaryMessageBus } from "./web_workers/shared/post_message_bus";
import { Serializer } from "./web_workers/shared/serializer";
import { ServiceMessageBrokerFactory } from "./web_workers/shared/service_message_broker";
import { ServiceMessageBrokerRegistry } from "./web_workers/shared/service_message_broker_registry";
import { ServiceMessageBrokerRegistryPrimary } from "./web_workers/shared/service_message_broker_registry_primary";
export { WORKER_PROVIDER_LOCATION_PROVIDERS } from "./web_workers/ui/location_providers";
import { NoOpRootRenderer } from "./web_workers/shared/renderer";

interface SharedWorker extends EventTarget, AbstractWorker {
  /**
   * Returns sharedWorker's MessagePort object which can be used to communicate with the global environment.
   */
  readonly port: MessagePort;
  addEventListener<K extends keyof AbstractWorkerEventMap>(
    type: K,
    listener: (this: SharedWorker, ev: AbstractWorkerEventMap[K]) => any,
    options?: boolean | AddEventListenerOptions,
  ): void;
  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void;
  removeEventListener<K extends keyof AbstractWorkerEventMap>(
    type: K,
    listener: (this: SharedWorker, ev: AbstractWorkerEventMap[K]) => any,
    options?: boolean | EventListenerOptions,
  ): void;
  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ): void;
  onconnect(event: { ports: MessagePort[] }): void;
}

declare let SharedWorker: {
  prototype: SharedWorker;
  new (scriptURL: string, options?: string | WorkerOptions): SharedWorker;
};

/**
 * Wrapper class that exposes the Worker
 * and underlying {@link MessageBus} for lower level message passing.
 *
 * @experimental WebWorker support is currently experimental.
 */
@Injectable()
export class WebWorkerInstance {
  public workers: Array<SharedWorker> = [];
  public bus: MessageBus;

  public setBus(bus: MessageBus) {
    this.bus = bus;
  }

  /** @internal */
  public addWorker(worker: SharedWorker) {
    this.workers.push(worker);
  }
}

/**
 * @experimental WebWorker support is currently experimental.
 */
export const WORKER_SCRIPT: InjectionToken<string> = new InjectionToken<string>("WebWorkerScript");

const WORKER_TOKEN_MAP: Map<string, InjectionToken<Response | Error | SharedWorker>> = new Map();
const WORKER_FETCH_RESPONSE_MAP: Map<string, Response | Error> = new Map();
const WORKER_REF_MAP: Map<string, SharedWorker> = new Map();

export const WORKER_FETCH_RESPONSE: (uri: string) => InjectionToken<Response | Error> = (uri) => {
  const tokenName = WORKER_FETCH_RESPONSE_NAME(uri);

  if (WORKER_TOKEN_MAP.has(tokenName)) {
    return WORKER_TOKEN_MAP.get(tokenName);
  }

  const token = new InjectionToken<Response | Error>(WORKER_FETCH_RESPONSE_NAME(uri), {
    factory: () => WORKER_FETCH_RESPONSE_MAP.get(WORKER_FETCH_RESPONSE_NAME(uri)),
  });

  WORKER_TOKEN_MAP.set(tokenName, token);

  return token;
};

export const WORKER_REF: (uri: string) => InjectionToken<SharedWorker> = (uri) => {
  const tokenName = WORKER_REF_NAME(uri);

  if (WORKER_TOKEN_MAP.has(tokenName)) {
    return WORKER_TOKEN_MAP.get(tokenName);
  }

  const token = new InjectionToken<SharedWorker>(tokenName, {
    factory: () => WORKER_REF_MAP.get(tokenName),
  });
  WORKER_TOKEN_MAP.set(tokenName, token);
  return token;
};
/**
 * A multi-provider used to automatically call the `start()` method after the service is
 * created.
 *
 * TODO(vicb): create an interface for startable services to implement
 * @experimental WebWorker support is currently experimental.
 */
export const WORKER_PROVIDER_STARTABLE_MESSAGING_SERVICE = new InjectionToken(
  "WorkerProviderStartableMsgService",
);

export const _WORKER_PROVIDER_PLATFORM_PROVIDERS: StaticProvider[] = [
  { provide: NgZone, useFactory: createNgZone, deps: [] },
  // {provide: NoOpRootRenderer, deps: []},
  {
    provide: WORKER_PROVIDER_STARTABLE_MESSAGING_SERVICE,
    useExisting: NoOpRootRenderer,
    multi: true,
  },
  { provide: ErrorHandler, useFactory: _exceptionHandler, deps: [] },
  {
    provide: ServiceMessageBrokerFactory,
    deps: [MessageBus, Serializer, DEBUG, ON_WEB_WORKER, ServiceMessageBrokerRegistry, NgZone],
  },
  {
    provide: ClientMessageBrokerFactory,
    deps: [MessageBus, Serializer, DEBUG, ServiceMessageBrokerRegistry, NgZone],
  },
  { provide: ON_WEB_WORKER, useValue: false },
  { provide: DEBUG, useValue: true },
  // {provide: Testability, deps: [ NgZone] },
  { provide: WebWorkerInstance, deps: [] },
  {
    provide: MessageBus,
    useFactory: messageBusFactory,
    deps: [WebWorkerInstance],
  },
  {
    provide: ServiceMessageBrokerRegistry,
    useClass: ServiceMessageBrokerRegistryPrimary,
    deps: [MessageBus, Serializer, DEBUG],
  },
];

function WORKER_REF_NAME(uri: string): string {
  return `SharedWorker:${uri}`;
}

function WORKER_FETCH_RESPONSE_NAME(uri: string): string {
  return `Fetch.response:${uri}`;
}

function messageBusFactory(instance: WebWorkerInstance): MessageBus {
  return instance.bus;
}

async function initWebWorker(injector: Injector, uri: string): Promise<void> {
  const instance = injector.get(WebWorkerInstance);
  const bus = new PrimaryMessageBus();
  instance.setBus(bus);

  const workerFetchResponse: Response | Error = await fetch(uri).catch((e) => {
    console.error(`worker_injector: initWebWorker fetch error: %O`, e);
    return e;
  });

  WORKER_FETCH_RESPONSE_MAP.set(WORKER_FETCH_RESPONSE_NAME(uri), workerFetchResponse);
  const workerRef = await spawnWebWorker(uri, instance, bus);
  WORKER_REF_MAP.set(WORKER_REF_NAME(uri), workerRef);
  const zone: NgZone = injector.get(NgZone);
  bus.attachToZone(zone);
}

/**
 * @experimental WebWorker support is currently experimental.
 */
export const platformWorkerInjector = _createInjector(
  "workerProvider",
  _WORKER_PROVIDER_PLATFORM_PROVIDERS,
);

function _createInjector(
  name: string,
  providers: StaticProvider[] = [],
): (scriptUri: Array<string>, extraProviders?: Provider[]) => Promise<Injector> {
  const marker = new InjectionToken(`Platform: ${name}`);

  return async (scriptUri, extraProviders?) => {
    if (!Array.isArray(scriptUri) || scriptUri.length === 0) {
      throw new Error(
        "You must provide your WebWorker's initialization script with the WORKER_SCRIPT token",
      );
    }

    const scriptUriProviders = scriptUri.flatMap((uri) => {
      return [{ provide: WORKER_REF(uri) }, { provide: WORKER_FETCH_RESPONSE(uri) }];
    });

    let injector: Injector = Injector.create({
      providers: providers
        .concat(extraProviders)
        .concat(scriptUriProviders)
        .concat({ provide: marker, useValue: true }),
    });

    for (const uri of scriptUri) {
      await initWebWorker(injector, uri);
    }

    return injector;
  };
}

function _exceptionHandler(): ErrorHandler {
  return new ErrorHandler();
}

function createNgZone(): NgZone {
  return new NgZone({ enableLongStackTrace: isDevMode() });
}

/**
 * Spawns a new class and initializes the WebWorkerInstance
 */
async function spawnWebWorker(
  uri: string,
  instance: WebWorkerInstance,
  bus: PrimaryMessageBus,
): Promise<SharedWorker> {
  if (module.hot && window[`__worker_${uri}`]) {
    const webWorker = window[`__worker_${uri}`];
    bus.addWorker(webWorker);
    instance.addWorker(webWorker);
    return webWorker;
  }

  // Forward `cubiq_trace=1` from the page URL into the SharedWorker URI so
  // diagnostics flags survive the realm boundary (the page hash is not
  // visible to the worker by default). Off unless the page URL carries it.
  const pageHref =
    (typeof window !== "undefined" &&
      ((window.location && window.location.href) || "")) ||
    "";
  const tracedUri = /cubiq_trace=1/.test(pageHref)
    ? uri + (uri.includes("?") ? "&" : "?") + "cubiq_trace=1"
    : uri;
  const webWorker: SharedWorker = new SharedWorker(tracedUri);

  if (module.hot) {
    window[`__worker_${uri}`] = webWorker;
  }

  bus.addWorker(webWorker);

  instance.addWorker(webWorker);

  return webWorker;
}
